import { Contract } from 'ethers';

// Canonical Multicall3 deployment, verified on BSC mainnet. One eth_call to
// this contract can execute many independent read-only contract calls.
const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11';
const MULTICALL3_ABI = [
  'function aggregate3((address target,bool allowFailure,bytes callData)[] calls) payable returns ((bool success,bytes returnData)[] returnData)',
] as const;

export type MulticallRead = {
  contract: Contract;
  method: string;
  args?: readonly unknown[];
  allowFailure?: boolean;
};

export async function multicallRead(calls: MulticallRead[]): Promise<any[]> {
  if (calls.length === 0) return [];
  const runner = calls[0].contract.runner;
  if (!runner) throw new Error('A blockchain provider is required for multicall reads.');
  const multicall = new Contract(MULTICALL3_ADDRESS, MULTICALL3_ABI, runner);
  const encoded = await Promise.all(
    calls.map(async (call) => ({
      target: await call.contract.getAddress(),
      allowFailure: call.allowFailure ?? false,
      callData: call.contract.interface.encodeFunctionData(call.method, call.args ?? []),
    }))
  );
  try {
    const results = await multicall.aggregate3.staticCall(encoded);
    return results.map((result: { success: boolean; returnData: string }, index: number) => {
      const call = calls[index];
      if (!result.success) {
        if (call.allowFailure) return null;
        throw new Error(`Multicall read failed: ${call.method}`);
      }
      const decoded = call.contract.interface.decodeFunctionResult(call.method, result.returnData);
      return decoded.length === 1 ? decoded[0] : decoded;
    });
  } catch (multicallError) {
    // Preserve compatibility if a future configured network lacks Multicall3.
    // This fallback is slower, but it keeps the dashboard functional.
    return Promise.all(
      calls.map(async (call) => {
        try {
          return await call.contract[call.method](...(call.args ?? []));
        } catch (error) {
          if (call.allowFailure) return null;
          throw multicallError instanceof Error ? multicallError : error;
        }
      })
    );
  }
}
